import { createSlice } from "@reduxjs/toolkit";
import {
  ISpecificationFoldersState,
  ISortConfig,
  FilterType,
  ItemType,
  IFolderContent,
} from "@/shared/services/interface/types/ISpecificationFoldersTypes";
import {
  getRootFoldersThunk,
  getFolderContentsThunk,
  createSpecificationFolderThunk,
  updateSpecificationFolderThunk,
  deleteSpecificationFolderThunk,
  createSpecificationThunk,
  getDeleteItemsFolderContentsThunk,
} from "../thunks/specification-folders-thunk";

const initialState: ISpecificationFoldersState = {
  rootFolders: [],
  rootLoading: false,
  rootError: null,
  rootPagination: { offset: 0, limit: 10, totalCount: 0 },
  sort: {
    sortBy: "createdAt",
    order: "DESC",
  },
  search: "",
  status: [],
  filter: "all",
  folderContents: {},
  newlyCreatedFolderId: null,
  newlyCreatedFolderParentId: null,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  createSpecificationLoading: false,
  newlyCreatedSpecificationParentId: null,
  newlyCreatedSpecificationId: null,
  selectedFolderIds: [],
  selectedSpecificationIds: [],
  itemsToDelete: [],
  deleteItemsFolderContents: {},
  deleteModalFolderIds: [],
  deleteModalSpecificationIds: [],
  isConfirmDelete: false,
};

export const specificationFoldersSlice = createSlice({
  name: "specificationFolders",
  initialState,
  reducers: {
    setSort: (state, action: { payload: Partial<ISortConfig> }) => {
      state.sort = { ...state.sort, ...action.payload };
    },
    setFilter: (state, action: { payload: FilterType }) => {
      state.filter = action.payload;
    },
    setRootFolders: (state, action) => {
      state.rootFolders = action.payload;
    },
    setFolderContent: (
      state,
      action: { payload: { folderId: string; content: any } }
    ) => {
      const { folderId, content } = action.payload;
      state.folderContents[folderId] = content;
    },
    setFolderLoading: (
      state,
      action: { payload: { folderId: string; isLoading: boolean } }
    ) => {
      const { folderId, isLoading } = action.payload;
      if (!state.folderContents[folderId]) {
        state.folderContents[folderId] = {
          items: [],
          pagination: { offset: 0, limit: 10, totalCount: 0 },
          isLoading: false,
          error: null,
        };
      }
      state.folderContents[folderId].isLoading = isLoading;
    },
    setFolderError: (
      state,
      action: { payload: { folderId: string; error: string | null } }
    ) => {
      const { folderId, error } = action.payload;
      if (!state.folderContents[folderId]) {
        state.folderContents[folderId] = {
          items: [],
          pagination: { offset: 0, limit: 10, totalCount: 0 },
          isLoading: false,
          error: null,
        };
      }
      state.folderContents[folderId].error = error;
    },
    setNewlyCreatedFolderId: (state, action) => {
      state.newlyCreatedFolderId = action.payload;
    },
    setNewlyCreatedFolderParentId: (state, action) => {
      state.newlyCreatedFolderParentId = action.payload;
    },
    clearNewlyCreatedFolderId: (state) => {
      state.newlyCreatedFolderId = null;
    },
    clearNewlyCreatedSpecificationParentId: (state) => {
      state.newlyCreatedSpecificationParentId = null;
    },
    clearNewlyCreatedFolderParentId: (state) => {
      state.newlyCreatedFolderParentId = null;
    },
    clearNewlyCreatedSpecificationId: (state) => {
      state.newlyCreatedSpecificationId = null;
    },
    clearFolderContent: (state, action: { payload: string }) => {
      delete state.folderContents[action.payload];
    },
    clearAllFolderContents: (state) => {
      state.folderContents = {};
    },
    setSelectedFolderIds: (state, action) => {
      state.selectedFolderIds = action.payload;
    },
    setSelectedSpecificationIds: (state, action) => {
      state.selectedSpecificationIds = action.payload;
    },
    clearSelectedFolderIds: (state) => {
      state.selectedFolderIds = [];
    },
    clearSelectedSpecificationIds: (state) => {
      state.selectedSpecificationIds = [];
    },
    setItemsToDelete: (state, action: { payload: ItemType[] }) => {
      state.itemsToDelete = action.payload;
    },
    setDeleteModalFolderIds: (state, action: { payload: string[] }) => {
      state.deleteModalFolderIds = action.payload;
    },
    setDeleteModalSpecificationIds: (state, action: { payload: string[] }) => {
      state.deleteModalSpecificationIds = action.payload;
    },
    clearItemsToDelete: (state) => {
      state.itemsToDelete = [];
    },
    setDeleteItemsFolderContent: (
      state,
      action: { payload: { folderId: string; content: IFolderContent } }
    ) => {
      const { folderId, content } = action.payload;
      state.deleteItemsFolderContents[folderId] = content;
    },
    setDeleteItemsFolderLoading: (
      state,
      action: { payload: { folderId: string; isLoading: boolean } }
    ) => {
      const { folderId, isLoading } = action.payload;
      if (!state.deleteItemsFolderContents[folderId]) {
        state.deleteItemsFolderContents[folderId] = {
          items: [],
          pagination: { offset: 0, limit: 10, totalCount: 0 },
          isLoading: false,
          error: null,
        };
      }
      state.deleteItemsFolderContents[folderId].isLoading = isLoading;
    },
    setDeleteItemsFolderError: (
      state,
      action: { payload: { folderId: string; error: string | null } }
    ) => {
      const { folderId, error } = action.payload;
      if (!state.deleteItemsFolderContents[folderId]) {
        state.deleteItemsFolderContents[folderId] = {
          items: [],
          pagination: { offset: 0, limit: 10, totalCount: 0 },
          isLoading: false,
          error: null,
        };
      }
      state.deleteItemsFolderContents[folderId].error = error;
    },
    clearDeleteItemsFolderContent: (state, action: { payload: string }) => {
      delete state.deleteItemsFolderContents[action.payload];
    },
    clearAllDeleteItemsFolderContents: (state) => {
      state.deleteItemsFolderContents = {};
    },
    setIsConfirmDelete: (state, action: { payload: boolean }) => {
      state.isConfirmDelete = action.payload;
    },
    resetSpecificationFoldersState: (state) => {
      state.selectedFolderIds = [];
      state.selectedSpecificationIds = [];
      state.rootFolders = [];
      state.rootLoading = false;
      state.rootError = null;
      state.rootPagination = { offset: 0, limit: 10, totalCount: 0 };
      state.sort = { sortBy: "createdAt", order: "DESC" };
      state.search = "";
      state.status = [];
      state.filter = "all";
      state.folderContents = {};
      state.newlyCreatedFolderId = null;
      state.newlyCreatedFolderParentId = null;
      state.createLoading = false;
      state.updateLoading = false;
      state.deleteLoading = false;
      state.createSpecificationLoading = false;
      state.newlyCreatedSpecificationParentId = null;
      state.newlyCreatedSpecificationId = null;
      state.itemsToDelete = [];
      state.deleteItemsFolderContents = {};
    },
  },
  extraReducers: (builder) => {
    // Get Root Folders
    builder
      .addCase(getRootFoldersThunk.pending, (state, action) => {
        const { filter, sortBy, order, hideLoadingEffect, status, search } =
          action.meta.arg;
        if (status !== undefined) {
          state.status = status ?? [];
        }
        if (search !== undefined) {
          state.search = search ?? "";
        }
        if (sortBy && order) {
          state.sort = { sortBy, order };
        }
        if (filter) {
          state.filter = filter;
        }
        if (!hideLoadingEffect) {
          state.rootLoading = true;
        }
        state.rootError = null;
      })
      .addCase(getRootFoldersThunk.fulfilled, (state, action) => {
        const { success, total, items } = action.payload as any;
        if (success) {
          const { offset, limit, hideLoadingEffect } = action.meta.arg;

          if (offset !== undefined && limit !== undefined) {
            state.rootPagination.offset = offset;
            state.rootPagination.limit = limit;
          }
          state.rootPagination.totalCount = total;
          if (!hideLoadingEffect) {
            state.rootLoading = false;
          }
          state.rootFolders = items;
        }
      })
      .addCase(getRootFoldersThunk.rejected, (state, action) => {
        const { hideLoadingEffect } = action.meta.arg;
        if (!hideLoadingEffect) {
          state.rootLoading = false;
        }
        state.rootError = action.payload as string;
      });

    // Get Folder Contents
    builder
      .addCase(getFolderContentsThunk.pending, (state, action) => {
        const {
          hideLoadingEffect,
          limit,
          folderId,
          loadMore,
          hideLoadingMoreEffect,
        } = action.meta.arg;
        if (loadMore && !hideLoadingMoreEffect) {
          state.folderContents[folderId].isLoadingMore = true;
        }
        if (loadMore) {
          state.folderContents[folderId].pagination.limit =
            limit ?? state.folderContents[folderId].pagination.limit;
          return;
        }
        state.folderContents[folderId] = {
          items: [],
          pagination: { offset: 0, limit: 10, totalCount: 0 },
          isLoading: hideLoadingEffect ? false : true,
          error: null,
        };
      })
      .addCase(getFolderContentsThunk.fulfilled, (state, action) => {
        const { folderId, response } = action.payload as any;
        const { limit } = action.meta.arg;
        if (!response.success) {
          state.folderContents[folderId].isLoadingMore = false;
          state.folderContents[folderId].isLoading = false;
          return;
        }
        if (response.success) {
          state.folderContents[folderId] = {
            items: response.items as ItemType[],
            pagination: {
              offset: 0,
              limit: limit ?? state.folderContents[folderId].pagination.limit,
              totalCount: response.total,
            },
            isLoadingMore: false,
            isLoading: false,
            error: null,
          };
        }
      })
      .addCase(getFolderContentsThunk.rejected, (state, action) => {
        const { folderId } = action.meta.arg;
        state.folderContents[folderId] = {
          ...state.folderContents[folderId],
          isLoading: false,
          isLoadingMore: false,
          error: (action.payload as string) ?? "Bir hata oluştu",
        };
      });

    // Create Specification Folder
    builder
      .addCase(createSpecificationFolderThunk.pending, (state) => {
        state.createLoading = true;
        state.rootError = null;
      })
      .addCase(createSpecificationFolderThunk.fulfilled, (state, action) => {
        state.createLoading = false;
        if (action.payload?.success) {
          // Yeni klasör oluşturulduğunda folders listesini güncelle
          const newFolder = action.payload.data;
          if (newFolder) {
            // Quick edit için yeni oluşturulan klasör id'sini set et
            state.newlyCreatedFolderId = newFolder.id;
            state.newlyCreatedFolderParentId = newFolder.parentId;
          }
        }
      })
      .addCase(createSpecificationFolderThunk.rejected, (state, action) => {
        state.createLoading = false;
        state.rootError = action.payload as string;
      });

    // Create Specification
    builder
      .addCase(createSpecificationThunk.pending, (state) => {
        state.createSpecificationLoading = true;
        state.rootError = null;
      })
      .addCase(createSpecificationThunk.fulfilled, (state, action) => {
        state.createSpecificationLoading = false;
        const { folderId } = action.meta.arg;

        if (action.payload?.success) {
          // Specification oluşturulduktan sonra root folders'ı yenile
          // Bu işlem genellikle parent component'te yapılır
          const specification = action.payload.data;
          if (specification) {
            state.newlyCreatedSpecificationParentId = folderId ?? null;
            state.newlyCreatedSpecificationId = specification.specification.id;
          }
        }
      })
      .addCase(createSpecificationThunk.rejected, (state, action) => {
        state.createSpecificationLoading = false;
        state.rootError = action.payload as string;
      });

    // Update Specification Folder
    builder
      .addCase(updateSpecificationFolderThunk.pending, (state) => {
        state.updateLoading = true;
        state.rootError = null;
      })
      .addCase(updateSpecificationFolderThunk.fulfilled, (state, action) => {
        state.updateLoading = false;
        if (action.payload?.success) {
          const updatedFolder = action.payload.data;
          const { parentId } = action.meta.arg;

          if (parentId === null) {
            // Root folder güncelleme
            const index = state.rootFolders.findIndex(
              (folder) => folder.id === updatedFolder.id
            );
            if (index !== -1) {
              state.rootFolders[index].name = updatedFolder.name;
            }
          } else {
            // Sub folder güncelleme
            if (state.folderContents[parentId]) {
              const index = state.folderContents[parentId].items.findIndex(
                (item) => item.id === updatedFolder.id
              );
              if (index !== -1) {
                state.folderContents[parentId].items[index].name =
                  updatedFolder.name;
              }
            }
          }
        }
      })
      .addCase(updateSpecificationFolderThunk.rejected, (state, action) => {
        state.updateLoading = false;
        state.rootError = action.payload as string;
      });

    // Delete Specification Folder
    builder
      .addCase(deleteSpecificationFolderThunk.pending, (state) => {
        state.deleteLoading = true;
        state.rootError = null;
      })
      .addCase(deleteSpecificationFolderThunk.fulfilled, (state) => {
        state.deleteLoading = false;
      })
      .addCase(deleteSpecificationFolderThunk.rejected, (state) => {
        state.deleteLoading = false;
      });

    // Get Delete Items Folder Contents
    builder
      .addCase(getDeleteItemsFolderContentsThunk.pending, (state, action) => {
        const {
          hideLoadingEffect,
          limit,
          folderId,
          loadMore,
          hideLoadingMoreEffect,
        } = action.meta.arg;
        if (loadMore && !hideLoadingMoreEffect) {
          if (!state.deleteItemsFolderContents[folderId]) {
            state.deleteItemsFolderContents[folderId] = {
              items: [],
              pagination: { offset: 0, limit: 10, totalCount: 0 },
              isLoading: false,
              isLoadingMore: false,
              error: null,
            };
          }
          state.deleteItemsFolderContents[folderId].isLoadingMore = true;
        }
        if (loadMore) {
          if (state.deleteItemsFolderContents[folderId]) {
            state.deleteItemsFolderContents[folderId].pagination.limit =
              limit ??
              state.deleteItemsFolderContents[folderId].pagination.limit;
          }
          return;
        }
        state.deleteItemsFolderContents[folderId] = {
          items: [],
          pagination: { offset: 0, limit: 10, totalCount: 0 },
          isLoading: hideLoadingEffect ? false : true,
          isLoadingMore: false,
          error: null,
        };
      })
      .addCase(getDeleteItemsFolderContentsThunk.fulfilled, (state, action) => {
        const { folderId, response } = action.payload as any;
        const { limit } = action.meta.arg;
        if (!response.success) {
          if (state.deleteItemsFolderContents[folderId]) {
            state.deleteItemsFolderContents[folderId].isLoadingMore = false;
            state.deleteItemsFolderContents[folderId].isLoading = false;
          }
          return;
        }
        if (response.success) {
          state.deleteItemsFolderContents[folderId] = {
            items: response.items as ItemType[],
            pagination: {
              offset: 0,
              limit:
                limit ??
                state.deleteItemsFolderContents[folderId]?.pagination?.limit ??
                10,
              totalCount: response.total,
            },
            isLoadingMore: false,
            isLoading: false,
            error: null,
          };
        }
      })
      .addCase(getDeleteItemsFolderContentsThunk.rejected, (state, action) => {
        const { folderId } = action.meta.arg;
        const errorPayload = action.payload as
          | { folderId: string; error: string }
          | string;
        const errorMessage =
          typeof errorPayload === "string"
            ? errorPayload
            : errorPayload?.error ?? "Bir hata oluştu";
        state.deleteItemsFolderContents[folderId] = {
          ...state.deleteItemsFolderContents[folderId],
          isLoading: false,
          isLoadingMore: false,
          error: errorMessage,
        };
      });
  },
});

export const {
  setSort,
  setFilter,
  setRootFolders,
  setFolderContent,
  setFolderLoading,
  setFolderError,
  setNewlyCreatedFolderId,
  setNewlyCreatedFolderParentId,
  clearNewlyCreatedFolderId,
  clearNewlyCreatedSpecificationParentId,
  clearNewlyCreatedFolderParentId,
  clearFolderContent,
  clearAllFolderContents,
  clearNewlyCreatedSpecificationId,
  setSelectedFolderIds,
  setSelectedSpecificationIds,
  clearSelectedFolderIds,
  clearSelectedSpecificationIds,
  setItemsToDelete,
  clearItemsToDelete,
  setDeleteItemsFolderContent,
  setDeleteItemsFolderLoading,
  setDeleteItemsFolderError,
  clearDeleteItemsFolderContent,
  clearAllDeleteItemsFolderContents,
  resetSpecificationFoldersState,
  setDeleteModalFolderIds,
  setDeleteModalSpecificationIds,
  setIsConfirmDelete,
} = specificationFoldersSlice.actions;

export default specificationFoldersSlice.reducer;
